import {
  isRoundV3,
  type RoundSnapshot,
  type RoundSnapshotV3,
} from "./round-types.js";
import type { RoundCommandPayload } from "./round-commands.js";
import { RoundCommandError } from "./round-errors.js";
import { setHoleEntry } from "./round-v3-commands.js";
import { selectContests } from "../contest/contest-selectors.js";
import { replaceContest } from "../contest/contest-state.js";

/** Physical scores remain provisional until the player finishes the round. */
export function canReviseProvisionalScore(
  snapshot: RoundSnapshot,
): snapshot is RoundSnapshotV3 {
  return (
    isRoundV3(snapshot) &&
    snapshot.state === "active" &&
    !snapshot.importedTotals &&
    snapshot.result === null &&
    snapshot.mainContest.format === "match-play" &&
    selectContests(snapshot).some((contest) => contest.result !== null) &&
    !snapshot.correctionProposals?.some(
      (proposal) => proposal.status === "pending",
    )
  );
}

/** Explicit command only: ordinary score writes keep their decided-result lock. */
export function reviseProvisionalHole(
  snapshot: RoundSnapshot,
  payload: RoundCommandPayload,
): RoundSnapshot {
  if (!canReviseProvisionalScore(snapshot))
    throw new RoundCommandError(
      "This result needs the result correction process. Only an unconfirmed main match can be revised directly.",
    );
  if (
    !("reviewedVersion" in payload) ||
    !("entry" in payload) ||
    payload.reviewedVersion !== snapshot.version
  )
    throw new RoundCommandError(
      "The scores changed. Reopen the scorecard before revising this score.",
    );
  let next = snapshot;
  for (const contest of selectContests(snapshot)) {
    const index = contest.holeNumbers.indexOf(payload.holeNumber);
    if (contest.result && index >= 0 && index < contest.result.holesPlayed)
      next = replaceContest(next, {
        ...contest,
        result: null,
        qualification: undefined,
        adjudication: undefined,
        dependencyReason: undefined,
      });
  }
  return setHoleEntry(next, payload);
}
